/**
 * Talking to the phone app around this page (plan.md step 60).
 *
 * Inside the MyRave app the page is in a web view, and the few things a page
 * cannot do — hold a native push token, schedule a notification that fires
 * with no network — it asks the shell for. The message shapes are
 * `@myrave/shared` `nativeBridge.ts`, read by both sides.
 */
import { parseFromNative, readNativeApp, readReminderLeads, type ArrivalRegion, type FromNative, type LiveCardMode, type LiveCardPlan, type PlannedReminder, type WidgetPlan, type WidgetWords } from '@myrave/shared';
import { readJson, writeJson } from './storage';
import { readPlatformCookie, signedInHere, writePlatformCookie } from './signOut';

interface RnWindow { ReactNativeWebView?: { postMessage: (m: string) => void } }

/** In the MyRave app, with its bridge present. */
export function inNativeApp(): boolean {
  if (typeof window === 'undefined') return false;
  return Boolean((window as unknown as RnWindow).ReactNativeWebView) && readNativeApp(navigator.userAgent) !== null;
}

function post(message: unknown): boolean {
  const rn = (window as unknown as RnWindow).ReactNativeWebView;
  if (!rn) return false;
  rn.postMessage(JSON.stringify(message));
  return true;
}

/**
 * In the phone app: tell the shell whether somebody is signed in (2026-10-26) — at launch it
 * reopens the last rave only for them; signed out it opens Find a rave. A browser: nothing.
 */
export function reportSession(signedIn: boolean, accountId?: string | null): void {
  // The account (step 79) only so the shell can tell one account's widgets from the next's —
  // it keeps a one-way hash of it, never the id.
  post({ type: 'session', signedIn, ...(signedIn && accountId ? { accountId } : {}) });
}

/* ------------------------------------------------------- the widgets --- */

/** One attended rave's data for the phone's widgets (step 79), from the app's offline data. */
export function postWidgetPlan(plan: WidgetPlan): void {
  post({ type: 'widget.plan', plan });
}

/** Take one rave out of every widget — or, with no rave, all of them. */
export function clearWidgets(editionId?: string): void {
  post({ type: 'widget.clear', ...(editionId ? { editionId } : {}) });
}

/** The raves this account attends, by the server's answer: every other rave leaves the widgets. */
export function keepWidgets(editionIds: readonly string[]): void {
  post({ type: 'widget.keep', editionIds: [...editionIds] });
}

/** The widgets' own words in the reader's language — signed out too ("Sign in to use MyRave widgets"). */
export function postWidgetWords(words: WidgetWords): void {
  post({ type: 'widget.words', words });
}

/**
 * In the phone app: may the phone turn sideways here? Only the schedule's
 * grid says yes (it goes full screen on its side); everywhere else the app
 * stays upright. Does nothing in a browser, which turns as it likes.
 */
export function allowRotation(allow: boolean): void {
  post({ type: 'orientation', allow: allow ? 'any' : 'portrait' });
}

/**
 * In the phone app: keep the screen on (the rave's map, the user, 2026-10-30 — the app no longer keeps
 * it on everywhere); `false` lets the phone sleep as it likes. A browser, or an older shell: nothing.
 */
export function keepScreenOn(on: boolean): void {
  post({ type: 'awake', on });
}

/**
 * In the phone app: the screen at full brightness while the invite's QR code shows (step 89), so
 * another phone can scan it in the dark — `false` gives the person their own brightness back. A
 * browser, or a shell from before it: nothing.
 */
export function raiseBrightness(raise: boolean): void {
  post({ type: 'brightness', raise });
}

function next(match: (m: FromNative) => boolean, timeoutMs: number): Promise<FromNative | null> {
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => { window.removeEventListener('myrave:native', on); resolve(null); }, timeoutMs);
    function on(e: Event) {
      const m = parseFromNative((e as CustomEvent).detail);
      if (!m || !match(m)) return;
      window.clearTimeout(timer);
      window.removeEventListener('myrave:native', on);
      resolve(m);
    }
    window.addEventListener('myrave:native', on);
  });
}

/**
 * Update the Android APK inside the app (the 0.14.74 walk): the shell downloads `url` with progress and opens
 * Android's installer. `'unanswered'` when the shell says nothing within `ANSWER_MS` (an older build, a Play build,
 * an iPhone) — the caller then opens the link; `'ready'` once the installer is up; `{ error }` when it failed.
 */
export const INSTALL_ANSWER_MS = 2000;
export function installUpdateInApp(url: string, onProgress: (done: number, total: number) => void): Promise<'unanswered' | 'ready' | { error: string }> {
  const requestId = `install-${Date.now()}`;
  return new Promise((resolve) => {
    let answered = false;
    const stop = (v: 'unanswered' | 'ready' | { error: string }) => {
      window.clearTimeout(timer); window.removeEventListener('myrave:native', on); resolve(v);
    };
    const timer = window.setTimeout(() => { if (!answered) stop('unanswered'); }, INSTALL_ANSWER_MS);
    function on(e: Event) {
      const m = parseFromNative((e as CustomEvent).detail);
      if (!m || !('requestId' in m) || m.requestId !== requestId) return;
      answered = true;
      window.clearTimeout(timer);
      if (m.type === 'app.install.progress') onProgress(m.done, m.total);
      else if (m.type === 'app.install.ready') stop('ready');
      else if (m.type === 'app.install.failed') stop({ error: m.message ?? 'error' });
    }
    window.addEventListener('myrave:native', on);
    if (!post({ type: 'app.install', requestId, url })) stop('unanswered');
  });
}

/**
 * Ask the shell for its push token (it asks the person first): the token, the
 * shell's refusal with its reason, or null when the shell did not answer.
 */
export async function nativePushToken(): Promise<FromNative | null> {
  const answer = next((m) => m.type === 'push.token' || m.type === 'push.denied', 30_000);
  if (!post({ type: 'push.register' })) return null;
  return answer;
}

let attestAsk = 0;
/**
 * Step 99: ask the shell to prove this is the genuine MyRave app (App Attest / Play Integrity) for the
 * server's one-time challenge. Null when the shell cannot (an older app, an emulator, an error) or says
 * nothing in time — the page then earns its pass the web's way (`lib/dataPass.ts`).
 */
export async function attestApp(challenge: string, cloudProjectNumber?: string): Promise<
  { platform: 'ios'; keyId: string; attestation: string } | { platform: 'android'; token: string } | null
> {
  const requestId = `attest-${Date.now()}-${++attestAsk}`;
  const answer = next((m) => (m.type === 'integrity.attested' || m.type === 'integrity.unavailable') && m.requestId === requestId, 8_000);
  if (!post({ type: 'integrity.attest', requestId, challenge, ...(cloudProjectNumber ? { cloudProjectNumber } : {}) })) return null;
  const m = await answer;
  if (m?.type !== 'integrity.attested') return null;
  return m.platform === 'ios'
    ? { platform: 'ios', keyId: m.keyId, attestation: m.attestation }
    : { platform: 'android', token: m.token };
}

/** The app shows Google's own account sheet for "Continue with Google" (the iPhone app, 2026-10-30). */
export function googleInApp(): boolean {
  return inNativeApp() && readNativeApp(navigator.userAgent)?.google === true;
}

let googleAsk = 0;
/**
 * Ask the app for Google's sign-in sheet: Google's ID token, minted for the platform's web client
 * (`webClientId`, from `/auth/providers`) — `'cancelled'` when the sheet was closed, null when Google or the
 * app failed (the caller then signs in the browser way). Two minutes: a person may be choosing an account.
 */
/**
 * Ask the app for Google's sheet: the token, `'cancelled'`, `{ error }` with Google's own words when the sheet
 * failed (the 0.14.74 walk on Android: a silent fall back to Chrome hid WHY — a missing Android client in Google
 * Cloud says `DEVELOPER_ERROR`), or null when the app did not answer at all.
 */
export async function googleSignInInApp(webClientId: string): Promise<{ idToken: string } | 'cancelled' | { error: string } | null> {
  const requestId = `google-${Date.now()}-${++googleAsk}`;
  const answer = next((m) => (m.type === 'google.signedin' || m.type === 'google.failed') && m.requestId === requestId, 120_000);
  if (!post({ type: 'google.signin', requestId, webClientId })) return null;
  const m = await answer;
  if (m?.type === 'google.signedin') return { idToken: m.idToken };
  if (m?.type === 'google.failed') return m.reason === 'cancelled' ? 'cancelled' : { error: m.message ?? 'error' };
  return null;
}

/** The app shows Apple's own sign-in sheet for "Continue with Apple" (the iPhone app, 2026-10-30). */
export function appleInApp(): boolean {
  return inNativeApp() && readNativeApp(navigator.userAgent)?.apple === true;
}

/** Ask the app for Apple's sheet: the token, code and name — `'cancelled'`, or null on a failure (as Google's). */
export async function appleSignInInApp(): Promise<Omit<Extract<FromNative, { type: 'apple.signedin' }>, 'type' | 'requestId'> | 'cancelled' | null> {
  const requestId = `apple-${Date.now()}-${++googleAsk}`;
  const answer = next((m) => (m.type === 'apple.signedin' || m.type === 'apple.failed') && m.requestId === requestId, 120_000);
  if (!post({ type: 'apple.signin', requestId })) return null;
  const m = await answer;
  if (m?.type === 'apple.signedin') {
    const { type: _t, requestId: _r, ...credential } = m;
    return credential;
  }
  return m?.type === 'apple.failed' && m.reason === 'cancelled' ? 'cancelled' : null;
}

let passkeyAsk = 0;
/**
 * An admin's passkey in the ANDROID app (2026-10-30): its web view has no WebAuthn, so the API's options go to
 * the shell as JSON, and Android's Credential Manager answers — a passkey on this phone, or one on another
 * device by QR code (the user's iPhone). The credential comes back as WebAuthn's own JSON, posted to the API
 * as a browser's. Throws as `navigator.credentials` does: a `NotAllowedError` when the sheet was closed or there
 * is no passkey, an `Error` when the app failed or said nothing. Five minutes: scanning a code takes a while.
 */
export async function passkeyInApp(kind: 'get' | 'create', options: unknown): Promise<Record<string, unknown>> {
  const requestId = `passkey-${Date.now()}-${++passkeyAsk}`;
  const answer = next((m) => (m.type === 'passkey.done' || m.type === 'passkey.failed') && m.requestId === requestId, 5 * 60_000);
  if (!post({ type: `passkey.${kind}`, requestId, requestJson: JSON.stringify(options) })) throw new Error('no app');
  const m = await answer;
  if (m?.type === 'passkey.done') return JSON.parse(m.responseJson) as Record<string, unknown>;
  if (m?.type === 'passkey.failed' && m.reason !== 'error') throw new DOMException('The operation was not allowed.', 'NotAllowedError');
  throw new Error(m?.type === 'passkey.failed' ? (m.message ?? 'passkey failed') : 'the app did not answer');
}

/** Hand the shell this rave's reminders; it replaces whatever it had scheduled for the rave. */
export function scheduleLocalReminders(editionId: string, reminders: PlannedReminder[]): void {
  post({ type: 'reminders.set', editionId, reminders });
}

/**
 * The reminder leads, kept on the phone too, so reminders are planned with no
 * signal. Nothing kept yet is the default (10 minutes); an older single number
 * is read as one (`readReminderLeads`).
 */
export function keptReminderMinutes(): number[] {
  let stored: unknown;
  try { stored = readJson<unknown>(null, 'reminderMinutes', undefined); } catch { stored = undefined; }
  return readReminderLeads(stored);
}

export function keepReminderMinutes(minutes: readonly number[]): void {
  writeJson(null, 'reminderMinutes', [...minutes]);
}

/**
 * When each saved set started as the phone last planned it — so a set that moved says "moved to
 * 22:00" (step 60). An account's only: signed out is online only, nothing kept is read and nothing
 * is kept (the user, 2026-10-27).
 */
export function keptReminderStarts<T>(editionId: string, none: T): T {
  return signedInHere() ? readJson<T>(editionId, 'reminder-starts', none) : none;
}

export function keepReminderStarts(editionId: string, starts: unknown): void {
  if (signedInHere()) writeJson(editionId, 'reminder-starts', starts);
}

/**
 * The account's muted artists and per-rave switches (step 61), kept on the
 * phone too — its LOCAL set reminders obey them with no signal.
 */
export interface NotificationScope {
  mutedArtists: string[];
  festivalTopics: Record<string, Record<string, false>>;
}

export function keptNotificationScope(): NotificationScope {
  let stored: unknown;
  try { stored = readJson<unknown>(null, 'notification-scope', undefined); } catch { stored = undefined; }
  const o = (stored && typeof stored === 'object' ? stored : {}) as Partial<NotificationScope>;
  return {
    mutedArtists: Array.isArray(o.mutedArtists) ? o.mutedArtists.filter((x): x is string => typeof x === 'string') : [],
    festivalTopics: o.festivalTopics && typeof o.festivalTopics === 'object' ? o.festivalTopics as NotificationScope['festivalTopics'] : {},
  };
}

export function keepNotificationScope(scope: Partial<NotificationScope>): void {
  writeJson(null, 'notification-scope', { mutedArtists: scope.mutedArtists ?? [], festivalTopics: scope.festivalTopics ?? {} });
}

/* ------------------------------------------------- the always-on card --- */

/** Show (or re-plan) this rave's always-on card; the shell keeps it current on its own (step 67). */
export function showLiveCard(plan: LiveCardPlan): void {
  post({ type: 'card.show', plan });
}

/** Take this rave's card off the lock screen. */
export function endLiveCard(editionId: string): void {
  post({ type: 'card.end', editionId });
}

/**
 * "Follow me": ask the shell for location in the background (`enable`), or
 * tell it to stop. Resolves with the shell's answer — null when it gave none
 * (an older app, or no app at all).
 */
export async function askLocationAlways(enable: boolean): Promise<{ granted: boolean; reason?: 'permission' | 'error' } | null> {
  const answer = next((m) => m.type === 'location.always', 60_000);
  if (!post({ type: 'location.always', enable })) return null;
  const m = await answer;
  return m && m.type === 'location.always' ? { granted: m.granted, ...(m.reason ? { reason: m.reason } : {}) } : null;
}

/** The card's switch and how it knows your stage — THIS phone's, not the account's. */
export interface LiveCardSetting { on: boolean; mode: LiveCardMode }
const CARD_DEFAULT: LiveCardSetting = { on: true, mode: 'last-stage' };
const CARD_COOKIE = 'myrave_live_card';
const CARD_KEY = 'live-card';

function cardSetting(raw: unknown): LiveCardSetting | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.on !== 'boolean' || (o.mode !== 'last-stage' && o.mode !== 'follow')) return null;
  return { on: o.on, mode: o.mode };
}

/**
 * Kept on the phone — and for EVERY host of it: the setting is chosen on
 * `account.` and read by each rave's own host, each its own origin with its
 * own `localStorage`. So the platform-domain cookie `myrave_live_card`
 * (`writePlatformCookie`, local and offline like the sign-out marker) is
 * what every host reads; `myrave:platform:live-card` stands in where cookies
 * are refused.
 */
export function keptLiveCard(): LiveCardSetting {
  const c = readPlatformCookie(CARD_COOKIE);
  const [on, mode] = (c ?? '').split('.');
  const fromCookie = c ? cardSetting({ on: on === 'on' ? true : on === 'off' ? false : null, mode }) : null;
  return fromCookie ?? cardSetting(readJson<unknown>(null, CARD_KEY, null)) ?? CARD_DEFAULT;
}

export function keepLiveCard(setting: LiveCardSetting): void {
  writeJson(null, CARD_KEY, setting);
  writePlatformCookie(CARD_COOKIE, `${setting.on ? 'on' : 'off'}.${setting.mode}`, 400 * 24 * 60 * 60);
}

/* ------------------------------------------- "you've arrived" (step 60) --- */

/**
 * Hand the shell the places to watch (`[]` stops it). Resolves with whether
 * it is watching — `on: false, reason: 'permission'` when "Always" location
 * was refused — or null when the app gave no answer (an older app).
 */
export async function setArrivalRegions(regions: ArrivalRegion[]): Promise<{ on: boolean; reason?: 'permission' | 'error' } | null> {
  const answer = next((m) => m.type === 'arrival.state', 60_000);
  if (!post({ type: 'arrival.set', regions })) return null;
  const m = await answer;
  return m && m.type === 'arrival.state' ? { on: m.on, ...(m.reason ? { reason: m.reason } : {}) } : null;
}

const ARRIVAL_COOKIE = 'myrave_arrival';
/**
 * "Tell me when I arrive at a rave" — THIS phone's, OFF until the person turns
 * it on (it needs "Always" location, which is theirs to give). Kept for every
 * host like the card's setting: the platform cookie, `myrave:platform:arrival`
 * where cookies are refused.
 */
export function keptArrivalAlerts(): boolean {
  const c = readPlatformCookie(ARRIVAL_COOKIE);
  if (c === 'on' || c === 'off') return c === 'on';
  return readJson<boolean>(null, 'arrival', false) === true;
}
export function keepArrivalAlerts(on: boolean): void {
  writeJson(null, 'arrival', on);
  writePlatformCookie(ARRIVAL_COOKIE, on ? 'on' : 'off', 400 * 24 * 60 * 60);
}

/* ------------------------------------------------------------- tickets (step 91) */

/**
 * Your tickets, mirrored into the APP's own store (`mobile/src/ticketStore.ts`): the one place every page shares
 * and the one a phone's transfer carries. A browser: nothing — tickets are the app's (the user, 2026-10-29).
 */
export function mirrorTicket(owner: string, id: string, editionId: string, ticket: unknown): void {
  if (inNativeApp()) post({ type: 'tickets.put', owner, id, editionId, ticket: JSON.stringify(ticket) });
}
export async function mirrorTicketFile(owner: string, id: string, editionId: string, blob: Blob, mime: string): Promise<void> {
  if (!inNativeApp()) return;
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  post({ type: 'tickets.file', owner, id, editionId, mime, base64: btoa(bin) });
}
export function mirrorTicketDrop(owner: string, ids: string[], fileIds: string[]): void {
  if (inNativeApp() && (ids.length || fileIds.length)) post({ type: 'tickets.drop', owner, ids, fileIds });
}
/**
 * A deleted ticket's TOMBSTONE in the app's store (2026-10-30) — through `tickets.put`, so today's phone build
 * keeps it with no change: id `gone:<ticket id>`, and a value that is not JSON (`tombstone:` + JSON), which a page
 * built before tombstones skips as "one unreadable row" instead of showing it as a ticket.
 */
export function mirrorTicketGone(owner: string, id: string, editionId: string, record: string): void {
  if (inNativeApp()) post({ type: 'tickets.put', owner, id, editionId, ticket: record });
}
export function mirrorTicketRaves(owner: string, raves: unknown): void {
  if (inNativeApp()) post({ type: 'tickets.raves', owner, raves: JSON.stringify(raves) });
}
let ticketAsk = 0;
/** What the app keeps for this account (the files named), or null with no app or no answer. */
export async function shellTickets(owner: string): Promise<Extract<FromNative, { type: 'tickets.list' }> | null> {
  if (!inNativeApp()) return null;
  const requestId = `tickets-${Date.now()}-${++ticketAsk}`;
  const answer = next((m) => m.type === 'tickets.list' && m.requestId === requestId, 10_000);
  post({ type: 'tickets.list', requestId, owner });
  const list = (await answer) as Extract<FromNative, { type: 'tickets.list' }> | null;
  // The app could not read its store: NO answer — never an empty one (an empty list is not "all deleted").
  return list && !list.failed ? list : null;
}
/** One file the app keeps, as a Blob, or null. */
export async function shellTicketFile(owner: string, id: string): Promise<Blob | null> {
  if (!inNativeApp()) return null;
  const requestId = `ticket-file-${Date.now()}-${++ticketAsk}`;
  const answer = next((m) => m.type === 'tickets.file' && m.requestId === requestId, 30_000);
  post({ type: 'tickets.fileGet', requestId, owner, id });
  const m = (await answer) as Extract<FromNative, { type: 'tickets.file' }> | null;
  if (!m?.base64 || !m.mime) return null;
  const bin = atob(m.base64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return new Blob([out], { type: m.mime });
}
